import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CaretLeft,
  CaretRight,
  Crosshair,
  ForkKnife,
  HandHeart,
  MagnifyingGlass,
  MapPin,
  MapTrifold,
  Pause,
  Play,
  Shield,
  UsersThree,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PageMeta } from '@/components/PageMeta';
import { StateFlagImage } from '@/components/StateFlagImage';
import { Footer } from '@/components/layout/Footer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { trackGeolocationDetected, trackSearch } from '@/lib/analytics';
import { US_STATES } from '@/lib/data';
import { logError } from '@/lib/error-monitoring';
import { getAllStatesAsync, getGeographicCacheInfo, type GeographicCacheInfo } from '@/lib/geographic-data';
import { requestUserLocation, reverseGeocodeCity } from '@/lib/geolocation-utils';
import { useI18n } from '@/lib/i18n';
import {
  classifyZipSearchCandidates,
  searchCityOrZip,
  searchCities,
  type CitySearchResult,
} from '@/lib/search-service';
import {
  getRenderableFlagCount,
  getVerifiedOfficialFlagCount,
  getWikimediaFallbackFlagCount,
} from '@/lib/state-flag-assets';
import type { State } from '@/lib/types';
import { isZipLikeInput, normalizeZipCode } from '@/lib/zip';

const TERRITORY_CODES = new Set(['DC', 'PR', 'VI']);
const STATE_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const stateDirectoryTotal = US_STATES.filter((state) => !TERRITORY_CODES.has(state.code)).length;
const nationwidePlaceCount = 31_916;
const verifiedFlagCount = getVerifiedOfficialFlagCount();
const renderableFlagCount = getRenderableFlagCount();
const wikimediaFallbackFlagCount = getWikimediaFallbackFlagCount();

const fallbackStates: State[] = US_STATES.filter((state) => !TERRITORY_CODES.has(state.code)).map((state) => ({
  ...state,
  counties: [],
  countyCount: 0,
  cityCount: 0,
}));

const dataRails = [
  { labelKey: 'home.railStates', value: String(stateDirectoryTotal), noteKey: 'home.railStateHubs' },
  { labelKey: 'home.railCounties', value: '3,142', noteKey: 'home.railCoverageModel' },
  { labelKey: 'home.railCities', value: nationwidePlaceCount.toLocaleString('en-US'), noteKey: 'home.railLocalPages' },
  { labelKey: 'home.railSnapRetailers', value: '260k+', noteKey: 'home.railUsdaData' },
] as const;

const quickRoutes = [
  {
    href: '/food',
    titleKey: 'home.trackFoodTitle',
    copyKey: 'home.trackFoodCopy',
    icon: ForkKnife,
  },
  {
    href: '/#state-directory',
    titleKey: 'home.stateDirectory',
    copyKey: 'home.quickStatesBody',
    icon: MapTrifold,
  },
  {
    href: '/community',
    titleKey: 'home.communityHub',
    copyKey: 'home.quickCommunityBody',
    icon: UsersThree,
  },
  {
    href: '/connecting-hands',
    titleKey: 'home.howToHelp',
    copyKey: 'home.quickHelpBody',
    icon: HandHeart,
  },
] as const;

const HERO_SLIDES = [
  {
    eyebrowKey: 'home.heroEyebrow',
    titleKey: 'home.heroTitle',
    bodyKey: 'home.heroBody',
    primaryHref: 'tel:211',
    primaryLabelKey: 'home.call211',
    secondaryHref: '/about',
    secondaryLabelKey: 'home.heroTrustLink',
    background:
      'radial-gradient(circle at 8% 4%, rgba(248, 206, 143, 0.22), transparent 30%), radial-gradient(circle at 92% 16%, rgba(126, 172, 146, 0.28), transparent 32%)',
  },
  {
    eyebrowKey: 'home.heroFoodEyebrow',
    titleKey: 'home.heroFoodTitle',
    bodyKey: 'home.heroFoodBody',
    primaryHref: '/food',
    primaryLabelKey: 'home.heroFoodAction',
    secondaryHref: '/#find-help',
    secondaryLabelKey: 'home.findSupport',
    background:
      'radial-gradient(circle at 10% 8%, rgba(239, 180, 111, 0.3), transparent 34%), radial-gradient(circle at 88% 12%, rgba(110, 154, 126, 0.28), transparent 36%)',
  },
  {
    eyebrowKey: 'home.heroTownEyebrow',
    titleKey: 'home.heroTownTitle',
    bodyKey: 'home.heroTownBody',
    primaryHref: '/#state-directory',
    primaryLabelKey: 'home.heroTownAction',
    secondaryHref: '/community',
    secondaryLabelKey: 'home.communityHub',
    background:
      'radial-gradient(circle at 12% 8%, rgba(116, 167, 145, 0.28), transparent 34%), radial-gradient(circle at 90% 18%, rgba(234, 176, 112, 0.24), transparent 34%)',
  },
] as const;

function HeroAction({ href, label, primary = false }: { href: string; label: string; primary?: boolean }) {
  const className = `inline-flex min-h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-full text-[11px] font-black transition sm:text-xs ${
    primary
      ? 'bg-[#fff8e7] px-3 text-[#173f31] shadow-[0_10px_24px_rgba(9,30,21,0.2)] hover:bg-white'
      : 'px-2 text-[#f6cc92] hover:bg-white/10'
  }`;
  const content = (
    <>
      {label}
      {primary && <ArrowRight size={13} weight="bold" />}
    </>
  );

  return href.startsWith('/') ? (
    <Link to={href} className={className}>
      {content}
    </Link>
  ) : (
    <a href={href} className={className}>
      {content}
    </a>
  );
}

function toRouteSlug(value: string): string {
  return value
    .trim()
    .replace(/\s+County$/i, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function getStateViewFromHash(): string | null {
  if (typeof window === 'undefined') return null;
  if (window.location.hash === '#states-all') return 'ALL';
  const hashMatch = window.location.hash.match(/^#states-([A-Z])$/u);
  return hashMatch?.[1] ?? null;
}

function getInitialStateLetter(): string {
  return getStateViewFromHash() ?? 'A';
}

async function loadStatesData(): Promise<State[]> {
  try {
    const states = await getAllStatesAsync();
    if (states.length > 0) return states;
  } catch (error) {
    console.error('[Data Load] Error loading the state directory:', error);
  }

  return fallbackStates;
}

function mergeWithFallbackStates(states: State[]): State[] {
  const byCode = new Map(states.map((state) => [state.code, state]));

  return fallbackStates.map((fallbackState) => ({
    ...fallbackState,
    ...byCode.get(fallbackState.code),
    counties: byCode.get(fallbackState.code)?.counties ?? [],
  }));
}

function getStateCounts(state: State) {
  const countyCount = state.countyCount ?? state.counties?.length ?? 0;
  const cityCount = state.cityCount ?? state.counties?.reduce((sum, county) => sum + county.cities.length, 0) ?? 0;
  return { countyCount, cityCount };
}

export function HomePage() {
  const { t } = useI18n();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<CitySearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [zipCandidates, setZipCandidates] = useState<CitySearchResult[]>([]);
  const [statesData, setStatesData] = useState<State[]>(fallbackStates);
  const [directoryHydrating, setDirectoryHydrating] = useState(true);
  const [searchFocused, setSearchFocused] = useState(false);
  const [cacheInfo, setCacheInfo] = useState<GeographicCacheInfo | null>(null);
  const [directoryQuery, setDirectoryQuery] = useState('');
  const [selectedLetter, setSelectedLetter] = useState(getInitialStateLetter);
  const [activeHeroSlide, setActiveHeroSlide] = useState(0);
  const [carouselPlaying, setCarouselPlaying] = useState(
    () => typeof window !== 'undefined' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  const deferredDirectoryQuery = useDeferredValue(directoryQuery);
  const heroSlide = HERO_SLIDES[activeHeroSlide];

  useEffect(() => {
    let active = true;

    async function hydrateDirectory() {
      const data = await loadStatesData();
      if (!active) return;

      setStatesData(mergeWithFallbackStates(data));
      setCacheInfo(await getGeographicCacheInfo());
      if (active) setDirectoryHydrating(false);
    }

    void hydrateDirectory();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!carouselPlaying) return;

    const timer = window.setInterval(() => {
      setActiveHeroSlide((current) => (current + 1) % HERO_SLIDES.length);
    }, 7_000);

    return () => window.clearInterval(timer);
  }, [carouselPlaying]);

  const stateDirectoryData = useMemo(
    () => mergeWithFallbackStates(statesData).filter((state) => !TERRITORY_CODES.has(state.code)),
    [statesData],
  );

  const availableLetters = useMemo(
    () => new Set(stateDirectoryData.map((state) => state.name.charAt(0).toUpperCase())),
    [stateDirectoryData],
  );
  const availableStateLetters = useMemo(
    () => STATE_LETTERS.filter((letter) => availableLetters.has(letter)),
    [availableLetters],
  );

  useEffect(() => {
    const syncStateViewFromHash = () => {
      const view = getStateViewFromHash();
      if (view && (view === 'ALL' || availableLetters.has(view))) {
        setSelectedLetter(view);
        setDirectoryQuery('');
      }
    };

    syncStateViewFromHash();
    window.addEventListener('hashchange', syncStateViewFromHash);
    return () => window.removeEventListener('hashchange', syncStateViewFromHash);
  }, [availableLetters]);

  const visibleStates = useMemo(() => {
    const query = deferredDirectoryQuery.trim().toLowerCase();

    if (query) {
      return stateDirectoryData.filter(
        (state) => state.name.toLowerCase().includes(query) || state.code.toLowerCase().includes(query),
      );
    }

    if (selectedLetter === 'ALL') return stateDirectoryData;
    return stateDirectoryData.filter((state) => state.name.startsWith(selectedLetter));
  }, [deferredDirectoryQuery, selectedLetter, stateDirectoryData]);

  useEffect(() => {
    let active = true;
    const term = searchQuery.trim();

    if (term.length < 2 || isZipLikeInput(term)) {
      setSuggestions([]);
      return () => {
        active = false;
      };
    }

    const timer = window.setTimeout(() => {
      void searchCities(term, 5).then((results) => {
        if (active) setSuggestions(results);
      });
    }, 180);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [searchQuery]);

  const handleSuggestionSelect = (result: CitySearchResult) => {
    const zip = normalizeZipCode(result.bestZip || result.zipCodes[0]);
    const url = `/${result.state}/${toRouteSlug(result.county)}/${result.slug}${zip ? `-${zip}` : ''}`;
    window.location.href = url;
  };

  const handleSearch = async (event: React.FormEvent) => {
    event.preventDefault();
    const query = searchQuery.trim();
    setSearchError('');

    if (!query) {
      setSearchError(t('home.searchEmpty'));
      return;
    }

    setIsSearching(true);
    try {
      const result = await searchCityOrZip(query);
      if (!result) {
        setSearchError(t('home.locationNotFound'));
        trackSearch(query, 0, undefined);
        return;
      }

      if (result.kind === 'zip' && classifyZipSearchCandidates(result.candidates, result.zip).kind === 'ambiguous') {
        setZipCandidates(result.candidates);
        return;
      }

      trackSearch(query, 1, undefined);
      const targetZip = normalizeZipCode(result.zip || result.city.zipCodes?.[0]);
      const url = `/${result.city.state}/${toRouteSlug(result.city.county)}/${result.city.slug}${
        targetZip ? `-${targetZip}` : ''
      }`;
      window.location.href = url;
    } catch (error) {
      console.error('Search error', error);
      logError({
        message: 'Search failed',
        stack: error instanceof Error ? error.stack : undefined,
        context: { query },
        severity: 'error',
      });
      setSearchError(t('home.searchFailed'));
    } finally {
      setIsSearching(false);
    }
  };

  const handleDetectLocation = async () => {
    setIsDetectingLocation(true);
    setSearchError('');

    try {
      toast.loading('Detecting your location...');
      const location = await requestUserLocation();
      const geocoded = await reverseGeocodeCity(location.lat, location.lng);

      if (geocoded?.city && geocoded.state) {
        toast.dismiss();
        toast.success(t('home.locationFound', { city: geocoded.city, state: geocoded.state }));
        trackGeolocationDetected(true, geocoded.city, geocoded.state, undefined);

        const searchResults = await searchCities(geocoded.city, 1);
        if (searchResults.length > 0) {
          handleSuggestionSelect(searchResults[0]);
        } else {
          setSearchQuery(`${geocoded.city}, ${geocoded.state}`);
          toast.info(t('home.tryLocation', { city: geocoded.city, state: geocoded.state }));
        }
      } else {
        toast.dismiss();
        toast.error(t('home.locationUnavailable'));
        setSearchError(t('home.locationUnavailable'));
        trackGeolocationDetected(false, undefined, undefined, undefined);
      }
    } catch (error) {
      toast.dismiss();
      const message = error instanceof Error ? error.message : 'Could not detect location';
      trackGeolocationDetected(false, undefined, undefined, undefined);
      logError({
        message: 'Geolocation detection failed',
        stack: error instanceof Error ? error.stack : undefined,
        context: { error: message },
        severity: 'warning',
      });
      setSearchError(message);
      toast.error(message);
    } finally {
      setIsDetectingLocation(false);
    }
  };

  const handleLetterSelect = (letter: string) => {
    setSelectedLetter(letter);
    setDirectoryQuery('');

    const hash = letter === 'ALL' ? '#states-all' : `#states-${letter}`;
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hash}`);
  };

  return (
    <div className="relative min-h-dvh overflow-hidden text-[#17231d]">
      <PageMeta title={t('home.metaTitle')} description={t('home.metaDescription')} />

      <main className="relative mx-auto max-w-[1380px] px-3 py-4 outline-none sm:px-5 sm:py-6 lg:px-7 lg:py-8">
        <section
          aria-label={t('home.carouselLabel')}
          aria-roledescription="carousel"
          className="relative overflow-hidden rounded-[1.6rem] border border-[#173f31]/15 bg-[#173f31] text-[#fff8e7] shadow-[0_24px_60px_rgba(23,63,49,0.2)] sm:rounded-[1.9rem]"
        >
          <div
            className="pointer-events-none absolute inset-0 opacity-90 transition-[background-image] duration-700"
            aria-hidden="true"
            style={{
              backgroundImage: `${heroSlide.background}, linear-gradient(135deg, rgba(255,255,255,0.07), transparent 46%)`,
            }}
          />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.08]"
            aria-hidden="true"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)',
              backgroundSize: '44px 44px',
            }}
          />

          <div className="relative flex flex-col gap-3 p-4 sm:p-5 lg:p-6">
            <div className="order-2 flex min-w-0 flex-col justify-between rounded-[1.35rem] border border-white/10 bg-[#123f31] p-4 sm:min-h-[220px] sm:p-3.5 lg:p-4">
              <div
                key={activeHeroSlide}
                className="peh-hero-slide"
                role="group"
                aria-roledescription="slide"
                aria-label={t('home.carouselSlideStatus', {
                  current: activeHeroSlide + 1,
                  total: HERO_SLIDES.length,
                })}
                aria-live={carouselPlaying ? 'off' : 'polite'}
              >
                <Badge className="w-fit max-w-full rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[9px] font-black uppercase tracking-[0.18em] text-[#f6cc92] shadow-none sm:text-[10px]">
                  {t(heroSlide.eyebrowKey)}
                </Badge>
                <h1 className="mt-3 max-w-3xl font-serif text-4xl font-black leading-[0.94] tracking-[-0.05em] text-[#fff8e7] sm:text-[2rem] lg:text-[2.65rem] xl:text-5xl">
                  {t(heroSlide.titleKey)}
                </h1>
                <p className="mt-2 max-w-2xl text-xs font-medium leading-5 text-[#dfe9df] lg:text-sm lg:leading-6 xl:text-base">
                  {t(heroSlide.bodyKey)}
                </p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <HeroAction href={heroSlide.primaryHref} label={t(heroSlide.primaryLabelKey)} primary />
                  <HeroAction href={heroSlide.secondaryHref} label={t(heroSlide.secondaryLabelKey)} />
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between gap-3 border-t border-white/12 pt-2.5">
                <div className="flex items-center gap-1" role="group" aria-label={t('home.carouselControls')}>
                  <button
                    type="button"
                    onClick={() =>
                      setActiveHeroSlide((current) => (current - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)
                    }
                    aria-label={t('home.carouselPrevious')}
                    className="grid h-8 w-8 place-items-center rounded-full border border-white/15 bg-white/8 text-white transition hover:bg-white/16"
                  >
                    <CaretLeft size={15} weight="bold" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCarouselPlaying((playing) => !playing)}
                    aria-label={carouselPlaying ? t('home.carouselPause') : t('home.carouselPlay')}
                    aria-pressed={!carouselPlaying}
                    className="grid h-8 w-8 place-items-center rounded-full bg-[#fff8e7] text-[#173f31] transition hover:bg-white"
                  >
                    {carouselPlaying ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveHeroSlide((current) => (current + 1) % HERO_SLIDES.length)}
                    aria-label={t('home.carouselNext')}
                    className="grid h-8 w-8 place-items-center rounded-full border border-white/15 bg-white/8 text-white transition hover:bg-white/16"
                  >
                    <CaretRight size={15} weight="bold" />
                  </button>
                </div>

                <div className="flex items-center gap-1.5">
                  {HERO_SLIDES.map((slide, index) => (
                    <button
                      type="button"
                      key={slide.titleKey}
                      onClick={() => setActiveHeroSlide(index)}
                      aria-label={t('home.carouselGoTo', { slide: index + 1 })}
                      aria-current={activeHeroSlide === index ? 'true' : undefined}
                      className={`h-1.5 rounded-full transition-all ${
                        activeHeroSlide === index ? 'w-6 bg-[#f6cc92]' : 'w-1.5 bg-white/35 hover:bg-white/60'
                      }`}
                    />
                  ))}
                  <span className="ml-1 text-[10px] font-black tabular-nums text-white/60">
                    {activeHeroSlide + 1}/{HERO_SLIDES.length}
                  </span>
                </div>
              </div>
            </div>

            <section
              id="find-help"
              className={`order-1 grid gap-3 rounded-[1.2rem] border border-white/20 bg-[#fffaf0] p-3 text-[#17231d] shadow-[0_18px_46px_rgba(7,24,17,0.2)] transition sm:grid-cols-[minmax(160px,0.45fr)_minmax(0,1.55fr)] sm:items-center ${
                searchFocused ? 'ring-4 ring-[#f3c78d]/30' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="order-first grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#173f31]/8 text-[#173f31]">
                  <MagnifyingGlass size={20} weight="bold" />
                </span>
                <div>
                  <p className="whitespace-nowrap text-[9px] font-black uppercase tracking-[0.14em] text-[#9a4d31]">
                    {t('home.commandSearch')}
                  </p>
                  <h2 className="mt-0.5 font-serif text-xl font-black leading-none tracking-[-0.045em] text-[#17231d]">
                    {t('home.startLocal')}
                  </h2>
                  <p className="mt-1 hidden max-w-md text-xs leading-5 text-[#657168] lg:block">
                    {t('home.searchDescription')}
                  </p>
                </div>
              </div>

              <form onSubmit={handleSearch} className="relative grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                <div className="relative">
                  <Input
                    type="text"
                    aria-label={t('home.searchPlaceholder')}
                    placeholder={t('home.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(event) => {
                      setSearchQuery(event.target.value);
                      setSearchError('');
                      setZipCandidates([]);
                    }}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    className="h-11 rounded-xl border-2 border-[#173f31]/12 bg-white pl-3 pr-20 text-sm font-bold text-[#17231d] shadow-inner placeholder:font-medium placeholder:text-[#858d86] focus:border-[#356a54]"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleDetectLocation}
                    disabled={isDetectingLocation || isSearching}
                    className="absolute right-9 top-1/2 -translate-y-1/2 rounded-full px-2 text-[#56645b] hover:bg-[#173f31]/8"
                    title={t('home.detectLocation')}
                    aria-label={t('home.detectLocation')}
                  >
                    <Crosshair
                      size={18}
                      weight={isDetectingLocation ? 'fill' : 'regular'}
                      className={isDetectingLocation ? 'animate-spin text-[#173f31]' : ''}
                    />
                  </Button>
                  <MapPin
                    size={18}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a55234]"
                    weight="fill"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSearching}
                  className="h-11 w-full rounded-xl bg-[#a14d31] px-5 text-sm font-black text-white shadow-[0_12px_24px_rgba(143,63,39,0.2)] hover:bg-[#893f28] sm:w-auto"
                >
                  {isSearching ? t('home.searching') : t('home.findSupport')}
                  <ArrowRight size={19} className="ml-2" weight="bold" />
                </Button>

                {suggestions.length > 0 && (
                  <div className="overflow-hidden rounded-2xl border border-[#173f31]/10 bg-white p-1.5 shadow-lg sm:absolute sm:left-0 sm:right-0 sm:top-[calc(100%+0.5rem)] sm:z-30">
                    {suggestions.map((suggestion) => (
                      <button
                        type="button"
                        key={`${suggestion.state}-${suggestion.county}-${suggestion.slug}`}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleSuggestionSelect(suggestion)}
                        className="w-full rounded-xl px-3 py-2.5 text-left transition hover:bg-[#edf3e8]"
                      >
                        <span className="block text-sm font-black text-[#17231d]">
                          {suggestion.name}, {suggestion.state}
                        </span>
                        <span className="block text-xs font-semibold text-[#677168]">
                          {suggestion.county} / ZIP {suggestion.bestZip || t('home.zipPending')}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {zipCandidates.length > 1 && (
                  <div
                    className="rounded-2xl border border-[#a55234]/20 bg-[#fff7ea] p-2 sm:absolute sm:left-0 sm:right-0 sm:top-[calc(100%+0.5rem)] sm:z-30"
                    role="region"
                    aria-label={t('home.zipChoices')}
                  >
                    <p className="px-2 pb-1 text-sm font-black text-[#17231d]">{t('home.zipMultiple')}</p>
                    {zipCandidates.map((candidate) => (
                      <button
                        type="button"
                        key={`${candidate.state}-${candidate.county}-${candidate.slug}`}
                        onClick={() => handleSuggestionSelect(candidate)}
                        className="w-full rounded-xl px-3 py-2.5 text-left transition hover:bg-[#edf3e8]"
                      >
                        <span className="block text-sm font-black text-[#17231d]">
                          {candidate.name}, {candidate.state}
                        </span>
                        <span className="block text-xs font-semibold text-[#677168]">
                          {candidate.county} / ZIP {candidate.bestZip}
                        </span>
                        {candidate.confidence && (
                          <span className="mt-1 block text-[10px] font-black uppercase tracking-wide text-[#955036]">
                            {candidate.confidence} {t('home.locationMatch')}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {searchError && (
                  <p
                    role="alert"
                    aria-live="assertive"
                    className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-bold text-red-800 sm:absolute sm:left-0 sm:right-0 sm:top-[calc(100%+0.5rem)] sm:z-30"
                  >
                    {searchError}
                  </p>
                )}
              </form>

              <div className="flex items-center justify-between gap-2 text-[10px] font-bold text-[#687168] sm:col-start-2 sm:-mt-2">
                <span className="inline-flex items-center gap-1.5">
                  <Shield size={14} weight="fill" className="text-[#397257]" />
                  {t('home.sourceFirst')}
                </span>
                {cacheInfo && (
                  <span className="truncate">
                    {t('home.dataSource')}: {cacheInfo.source}
                  </span>
                )}
              </div>
            </section>
          </div>

          <div className="relative grid grid-cols-2 border-t border-white/12 bg-black/10 sm:grid-cols-4">
            {dataRails.map((rail, index) => (
              <div
                key={rail.labelKey}
                className={`px-3 py-3 text-center ${
                  index % 2 === 0 ? 'border-r border-white/12' : ''
                } sm:border-r sm:last:border-r-0`}
              >
                <div className="flex items-baseline justify-center gap-1.5">
                  <span className="font-serif text-2xl font-black tracking-[-0.05em] text-[#fff8e7]">{rail.value}</span>
                  <span className="text-[9px] font-black uppercase tracking-[0.18em] text-[#f3c78d]">
                    {t(rail.labelKey)}
                  </span>
                </div>
                <p className="mt-0.5 text-[11px] font-semibold text-white/58">{t(rail.noteKey)}</p>
              </div>
            ))}
          </div>
        </section>

        <section aria-label={t('home.quickRoutes')} className="mt-4 grid grid-cols-2 gap-2 xl:grid-cols-4">
          {quickRoutes.map((route) => {
            const Icon = route.icon;
            return (
              <Link
                key={route.href}
                to={route.href}
                className="group flex min-h-20 items-center gap-3 rounded-[1.35rem] border border-[#173f31]/10 bg-[#fffaf0]/76 p-3 shadow-[0_12px_30px_rgba(38,50,40,0.06)] backdrop-blur transition hover:-translate-y-0.5 hover:border-[#173f31]/20 hover:bg-[#fffaf0] hover:shadow-[0_18px_36px_rgba(38,50,40,0.1)] sm:min-h-24 sm:p-4"
              >
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#173f31]/8 text-[#356a54] transition group-hover:bg-[#173f31] group-hover:text-[#fff8e7]">
                  <Icon size={22} weight="duotone" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-black text-[#26362e]">{t(route.titleKey)}</span>
                  <span className="mt-1 hidden text-xs leading-5 text-[#69736b] sm:block">{t(route.copyKey)}</span>
                </span>
                <ArrowRight
                  size={17}
                  weight="bold"
                  className="ml-auto shrink-0 text-[#9a553a] transition-transform group-hover:translate-x-1"
                />
              </Link>
            );
          })}
        </section>

        <section
          id="state-directory"
          className="mt-5 scroll-mt-24 overflow-hidden rounded-[1.6rem] border border-[#173f31]/10 bg-[#fffaf0]/82 shadow-[0_20px_50px_rgba(38,50,40,0.09)] backdrop-blur-xl sm:rounded-[1.9rem]"
        >
          <div className="border-b border-[#173f31]/10 p-4 sm:p-5">
            <div className="mx-auto max-w-4xl text-center">
              <div className="flex flex-col items-center">
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#985039]">
                  {t('home.stateDirectory')}
                </p>
                <h2 className="mt-1.5 max-w-3xl font-serif text-3xl font-black leading-[0.96] tracking-[-0.045em] text-[#17231d] sm:text-4xl">
                  {t('home.browseAllStates')}
                </h2>
                <p className="mt-2 max-w-2xl text-sm leading-5 text-[#647068]">{t('home.directoryDescription')}</p>
              </div>
              <div className="mt-3 flex flex-wrap justify-center gap-1.5" aria-label={t('home.directoryStatus')}>
                <Badge className="rounded-full bg-[#173f31] px-3 py-1 text-[11px] font-black text-white shadow-none">
                  {stateDirectoryTotal}/50 states
                </Badge>
                <Badge
                  variant="outline"
                  className="rounded-full border-[#173f31]/15 bg-white/70 px-3 py-1 text-[11px] font-black text-[#173f31]"
                >
                  {renderableFlagCount}/50 flags
                </Badge>
                <Badge
                  variant="outline"
                  className="rounded-full border-[#173f31]/15 bg-white/70 px-3 py-1 text-[11px] font-black text-[#173f31]"
                >
                  {verifiedFlagCount} official / {wikimediaFallbackFlagCount} wiki
                </Badge>
              </div>
            </div>

            <div className="mx-auto mt-4 grid max-w-4xl gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(210px,0.42fr)]">
              <div className="relative">
                <MagnifyingGlass
                  size={19}
                  weight="bold"
                  className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#617068]"
                />
                <Input
                  type="search"
                  value={directoryQuery}
                  onChange={(event) => setDirectoryQuery(event.target.value)}
                  placeholder={t('home.filterStatePlaceholder')}
                  aria-label={t('home.filterStatePlaceholder')}
                  className="h-11 rounded-xl border-[#173f31]/12 bg-white/80 pl-11 pr-4 text-sm font-bold shadow-inner focus:border-[#356a54]"
                />
              </div>
              <label className="sr-only" htmlFor="state-jump-select">
                {t('home.chooseState')}
              </label>
              <select
                id="state-jump-select"
                defaultValue=""
                onChange={(event) => {
                  if (event.target.value) window.location.href = `/${event.target.value}`;
                }}
                className="h-11 w-full rounded-xl border border-[#173f31]/12 bg-white/80 px-3 text-sm font-black text-[#26362e] outline-none focus:border-[#356a54] focus:ring-4 focus:ring-[#356a54]/15"
              >
                <option value="">{t('home.chooseState')}</option>
                {stateDirectoryData.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.name} ({state.code})
                  </option>
                ))}
              </select>
            </div>

            <nav aria-label={t('home.alphabeticalStateNavigation')} className="mx-auto mt-3 max-w-4xl overflow-hidden">
              <div className="flex gap-1 overflow-x-auto pb-2">
                <button
                  type="button"
                  onClick={() => handleLetterSelect('ALL')}
                  aria-pressed={selectedLetter === 'ALL' && !directoryQuery}
                  className={`min-h-8 shrink-0 rounded-lg px-2.5 text-[10px] font-black uppercase tracking-wider transition ${
                    selectedLetter === 'ALL' && !directoryQuery
                      ? 'bg-[#173f31] text-white shadow-md'
                      : 'border border-[#173f31]/10 bg-white/65 text-[#536159] hover:bg-[#173f31]/8'
                  }`}
                >
                  {t('home.allStates')}
                </button>
                {availableStateLetters.map((letter) => {
                  const active = selectedLetter === letter && !directoryQuery;
                  return (
                    <button
                      type="button"
                      key={letter}
                      onClick={() => handleLetterSelect(letter)}
                      aria-pressed={active}
                      aria-label={t('home.statesBeginningWith', { letter })}
                      className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[11px] font-black transition ${
                        active
                          ? 'bg-[#a14d31] text-white shadow-md'
                          : 'border border-[#173f31]/10 bg-white/65 text-[#3f4e45] hover:bg-[#173f31]/8'
                      }`}
                    >
                      {letter}
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>

          <div>
            <div className="hidden">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#8d5038]">
                  {directoryQuery ? t('home.searchResults') : t('home.selectedView')}
                </p>
                <p className="mt-2 font-serif text-4xl font-black leading-none tracking-[-0.05em] text-[#173f31]">
                  {directoryQuery
                    ? visibleStates.length
                    : selectedLetter === 'ALL'
                      ? stateDirectoryTotal
                      : selectedLetter}
                </p>
                <p className="mt-2 text-sm font-bold leading-5 text-[#506057]">
                  {directoryQuery
                    ? t('home.stateMatches', { count: visibleStates.length })
                    : selectedLetter === 'ALL'
                      ? t('home.allStateHubs')
                      : t('home.statesBeginningWith', { letter: selectedLetter })}
                </p>
              </div>
              <div className="hidden rounded-2xl border border-[#173f31]/10 bg-white/65 p-3 lg:mt-5 lg:block">
                <div className="flex items-start gap-2">
                  <MapTrifold size={18} weight="duotone" className="mt-0.5 shrink-0 text-[#46705d]" />
                  <p className="text-xs leading-5 text-[#637067]">{t('home.stateBrowseTip')}</p>
                </div>
              </div>
            </div>

            <div className="min-w-0 p-4">
              <div className="mb-3 flex items-center justify-between gap-3 px-1">
                <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.14em] text-[#59675e]">
                  {!directoryQuery && selectedLetter !== 'ALL' && (
                    <span className="grid h-7 w-7 place-items-center rounded-lg bg-[#a14d31] text-[11px] text-white">
                      {selectedLetter}
                    </span>
                  )}
                  <span>
                    {visibleStates.length} {visibleStates.length === 1 ? t('home.stateResult') : t('home.stateResults')}
                  </span>
                </p>
                <span className="text-[11px] font-bold text-[#59665e]" aria-live="polite">
                  {directoryHydrating ? t('home.coverageLoading') : t('home.coverageReady')}
                </span>
              </div>

              <div
                id={selectedLetter === 'ALL' ? 'states-all' : `states-${selectedLetter}`}
                className="max-h-[42rem] overflow-y-auto overscroll-contain pr-1"
                aria-busy={directoryHydrating}
              >
                {visibleStates.length > 0 ? (
                  <div className="grid gap-2 md:grid-cols-2">
                    {visibleStates.map((state) => {
                      const { countyCount, cityCount } = getStateCounts(state);
                      const hasCoverageMetrics = countyCount > 0 || cityCount > 0;

                      return (
                        <Link
                          key={state.code}
                          to={`/${state.code}`}
                          aria-label={t('home.openStateHub', { state: state.name })}
                          className="group flex min-h-[82px] items-center gap-3 rounded-[1.25rem] border border-[#173f31]/10 bg-white/68 p-3 transition hover:border-[#173f31]/20 hover:bg-white hover:shadow-[0_14px_28px_rgba(36,48,39,0.09)] focus-visible:ring-4 focus-visible:ring-[#a14d31]/25"
                        >
                          <span className="h-14 w-[4.75rem] shrink-0 overflow-hidden rounded-xl border border-[#173f31]/10 bg-[#f3eee3] shadow-sm">
                            <StateFlagImage
                              stateCode={state.code}
                              stateName={state.name}
                              showSourceBadge={false}
                              className="rounded-xl"
                            />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2">
                              <span className="truncate text-base font-black text-[#26362e]">{state.name}</span>
                              <span className="rounded-md bg-[#173f31]/7 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-[#557063]">
                                {state.code}
                              </span>
                            </span>
                            <span className="mt-1 block truncate text-xs font-semibold text-[#6a746c]">
                              {hasCoverageMetrics
                                ? t('home.stateCoverageCounts', {
                                    counties: countyCount.toLocaleString('en-US'),
                                    cities: cityCount.toLocaleString('en-US'),
                                  })
                                : directoryHydrating
                                  ? t('home.coverageLoading')
                                  : t('home.openForCoverage')}
                            </span>
                          </span>
                          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#173f31]/7 text-[#356a54] transition group-hover:bg-[#173f31] group-hover:text-white">
                            <ArrowRight size={17} weight="bold" />
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="grid min-h-52 place-items-center rounded-2xl border border-dashed border-[#173f31]/18 bg-white/45 p-6 text-center">
                    <div>
                      <MagnifyingGlass size={30} weight="duotone" className="mx-auto text-[#557063]" />
                      <p className="mt-3 text-base font-black text-[#26362e]">{t('home.noStateMatches')}</p>
                      <button
                        type="button"
                        onClick={() => setDirectoryQuery('')}
                        className="mt-2 text-sm font-black text-[#995038] hover:underline"
                      >
                        {t('home.clearStateFilter')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="mt-5 overflow-hidden rounded-[1.4rem] border border-[#173f31]/12 bg-[#173f31] text-[#fff8e7] shadow-[0_16px_38px_rgba(23,63,49,0.16)]">
          <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/10 text-[#f3c78d]">
                <Shield size={20} weight="fill" />
              </span>
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#f3c78d]">
                  {t('home.immediateHelpLabel')}
                </p>
                <p className="mt-1 font-serif text-xl font-black leading-tight tracking-[-0.03em]">
                  {t('home.immediateHelpBody')}
                </p>
                <p className="mt-1.5 max-w-3xl text-[11px] leading-4 text-white/68" role="note">
                  {t('home.emergencyNote')}
                </p>
              </div>
            </div>
            <Link
              to="/county-verification"
              className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-[#fff8e7] px-4 text-xs font-black text-[#173f31] transition hover:bg-white"
            >
              {t('home.viewCoverage')}
              <ArrowRight size={17} weight="bold" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
